import { X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { DashboardStats, QuickStartGuide, UserMenu } from '../components';
import { AnnouncementCard, type Announcement } from '@/components/AnnouncementCard';
import { NotificationSettings } from '@/components/NotificationSettings';
import { ThemeToggle } from '@/components/ui';
import { AlertIcon } from '@/icons';
import { createApiClient } from '@/lib/api-client';
import { useApiStore } from '@/store/api';
import { useAuthStore } from '@/store/auth';

/** Convert raw API/network errors into user-friendly dashboard messages */
function normalizeDashboardError(err: unknown): string {
  if (!(err instanceof Error)) {
    return 'Unable to load dashboard data. Please refresh the page.';
  }

  const msg = err.message;

  // JSON parse errors (API returned HTML instead of JSON)
  if (msg.includes('Unexpected token') || msg.includes('is not valid JSON')) {
    return 'Unable to load dashboard data. The server returned an unexpected response.';
  }

  // Network errors
  if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
    return 'Could not reach the server. Check your connection and try again.';
  }

  // Timeout
  if (err.name === 'AbortError' || msg.includes('aborted') || msg.includes('timed out')) {
    return 'The request timed out. Please refresh the page.';
  }

  // Generic fallback — keep it friendly
  return 'Unable to load dashboard data. Please refresh the page.';
}

interface DashboardData {
  agentCount: number;
  conversationCount: number;
}

/**
 * WelcomeText - User welcome message
 * Extracted component to reduce JSX nesting depth
 */
function WelcomeText({ username }: { username: string }) {
  return (
    <span className="text-neutral-700 dark:text-neutral-300 hidden sm:inline">
      Welcome,{' '}
      <strong className="font-medium text-neutral-900 dark:text-neutral-100">{username}</strong>
    </span>
  );
}

// Announcements can be fetched from API or defined statically
// For now, we show a welcome tip for new users
function getAnnouncements(isFirstTimeUser: boolean): Announcement[] {
  const announcements: Announcement[] = [];

  // Welcome tip for new users
  if (isFirstTimeUser) {
    announcements.push({
      id: 'welcome-tip-v1',
      type: 'info',
      title: 'Welcome to QuarkChat!',
      message: 'Get started by creating your first agent and connecting it to a conversation.',
      dismissible: true,
    });
  }

  // Example: Maintenance announcement (uncomment when needed)
  // announcements.push({
  //   id: 'maintenance-2025-01',
  //   type: 'maintenance',
  //   title: 'Scheduled Maintenance',
  //   message: 'We will be performing maintenance on January 20th from 2-4am UTC.',
  //   dismissible: true,
  // });

  return announcements;
}

export function DashboardPage() {
  const { user, logout, isAuthenticated } = useAuthStore();
  const apiUrl = useApiStore((state) => state.apiUrl);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    agentCount: 0,
    conversationCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const client = createApiClient(apiUrl);

        // Fetch all data in parallel
        const [agentsResult, conversationsResult] = await Promise.all([
          client.GET('/v1/agents'),
          client.GET('/v1/conversations'),
        ]);

        if (
          agentsResult.error ||
          !agentsResult.data ||
          conversationsResult.error ||
          !conversationsResult.data
        ) {
          throw new Error('Failed to fetch dashboard data');
        }

        const data = {
          agentCount: agentsResult.data.agents?.length || 0,
          conversationCount: conversationsResult.data.total || 0,
        };

        setDashboardData(data);
        setIsFirstTimeUser(data.agentCount === 0 && data.conversationCount === 0);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(normalizeDashboardError(err));
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isAuthenticated, apiUrl]);

  const announcements = getAnnouncements(isFirstTimeUser);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-300 dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                QuarkChat
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <WelcomeText username={user?.username || ''} />
              <ThemeToggle />
              <UserMenu username={user?.username || ''} onLogout={logout} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Announcements */}
        <AnnouncementCard announcements={announcements} />

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-300 dark:border-red-700 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <AlertIcon className="text-red-600 dark:text-red-400 flex-shrink-0" />
              <p className="text-red-800 dark:text-red-200 flex-1">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 p-1 rounded transition-colors flex-shrink-0"
                aria-label="Dismiss error"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <DashboardStats
          agentCount={dashboardData.agentCount}
          conversationCount={dashboardData.conversationCount}
          loading={loading}
        />

        {/* Notification Settings */}
        <div className="mb-8">
          <NotificationSettings />
        </div>

        {/* Quick Start Guide */}
        <QuickStartGuide />
      </main>
    </div>
  );
}
